import { MAINTENANCE_BANNER_LOGIN, MAINTENANCE_BANNER_MODULE, MARKET_NAMES } from '@/constants/const';
import { AuthType } from '@/types/types';
import { NextRouter } from 'next/router';
import { getCurrentCountryCode } from '@/utils/commonUtils';
import { ROUTES_URL } from '@/constants/routesConst';

export function filterPreLoginBanners(banners: Record<string, any>[] = []) {
    return banners.filter(
        (banner) =>
            banner?.login?.value === MAINTENANCE_BANNER_LOGIN.PRE_LOGIN ||
            banner?.login?.value === MAINTENANCE_BANNER_LOGIN.PRE_AND_POST_LOGIN
    );
}

export function filterPostLoginBanners(banners: Record<string, any>[] = []) {
    return banners.filter(
        (banner) =>
            banner?.login?.value === MAINTENANCE_BANNER_LOGIN?.POST_LOGIN ||
            banner?.login?.value === MAINTENANCE_BANNER_LOGIN?.PRE_AND_POST_LOGIN
    );
}

export function filterBannersByModule(banners: Record<string, any>[] = [], module: string) {
    return banners.filter((banner) =>
        banner.applicableModules.value.map((module) => module.displayName).includes(module)
    );
}

export function filterWidgetsByPage(widgets: Record<string, any>[] = [], page?: string) {
    return widgets.filter((widget) => widget.location.value?.map((page) => page.displayName).includes(page));
}

export type FilterBannersParams = {
    asPath: NextRouter['asPath'];
    authOk: AuthType['ok'];
    maintenanceBanners: Record<string, any>[];
};

export function getFilterBanners({ asPath, authOk, maintenanceBanners }: FilterBannersParams) {
    const isKRMarket = getCurrentCountryCode() === MARKET_NAMES.HK;

    if (!authOk) return filterPreLoginBanners(maintenanceBanners ?? []);

    const filteredMaintenanceBannersPostLogin = filterPostLoginBanners(maintenanceBanners ?? []);

    const isOrdersModule =
        asPath !== ROUTES_URL.CONFIRM_PAYMENT_ORDER &&
        asPath !== ROUTES_URL.CONFIRM_PAYMENT_ORDER &&
        asPath !== ROUTES_URL.RETURNS &&
        !asPath?.includes(ROUTES_URL.NEW_PAYMENT) &&
        !asPath?.includes(ROUTES_URL.PAYMENT_CLAIM_SUBMISSION) &&
        !asPath?.includes(ROUTES_URL.CREDITNOTE_DETAILS) &&
        !asPath?.includes(ROUTES_URL.PAYMENT_INVOICE_DETAILS) &&
        !asPath?.includes(ROUTES_URL.INVOICE_DETAILS) &&
        !asPath?.includes(ROUTES_URL.PAYMENT_LISTING) &&
        !asPath?.includes(ROUTES_URL.PAYMENT_SUMMARY) &&
        !asPath?.includes(ROUTES_URL.PAYMENT_METHOD) &&
        !asPath?.includes(ROUTES_URL.PAYMENT_THANK_YOU) &&
        !asPath?.includes(ROUTES_URL.RETURN_SUMMARY_DETAILS) &&
        !asPath?.includes(ROUTES_URL.NEW_RETURN_REQUEST);

    const isPaymentsModule =
        asPath === ROUTES_URL.CONFIRM_PAYMENT_ORDER ||
        asPath?.includes(ROUTES_URL.NEW_PAYMENT) ||
        asPath?.includes(ROUTES_URL.PAYMENT_CLAIM_SUBMISSION) ||
        asPath?.includes(ROUTES_URL.CREDITNOTE_DETAILS) ||
        asPath?.includes(ROUTES_URL.PAYMENT_INVOICE_DETAILS) ||
        asPath?.includes(ROUTES_URL.INVOICE_DETAILS) ||
        asPath?.includes(ROUTES_URL.PAYMENT_LISTING) ||
        asPath?.includes(ROUTES_URL.PAYMENT_SUMMARY) ||
        asPath?.includes(ROUTES_URL.PAYMENT_METHOD) ||
        asPath?.includes(ROUTES_URL.PAYMENT_THANK_YOU);

    const isReturnsModule =
        asPath === ROUTES_URL.RETURNS ||
        asPath?.includes(ROUTES_URL.RETURN_SUMMARY_DETAILS) ||
        asPath?.includes(ROUTES_URL.NEW_RETURN_REQUEST);

    if (isKRMarket || isOrdersModule) {
        return filterBannersByModule(filteredMaintenanceBannersPostLogin, MAINTENANCE_BANNER_MODULE.ORDERS);
    }

    if (isPaymentsModule) {
        return filterBannersByModule(filteredMaintenanceBannersPostLogin, MAINTENANCE_BANNER_MODULE.PAYMENTS);
    }

    if (isReturnsModule) {
        return filterBannersByModule(filteredMaintenanceBannersPostLogin, MAINTENANCE_BANNER_MODULE.RETURNS);
    }

    return null;
}

export type GetProductWidgetPageTitleParams = {
    asPath: NextRouter['asPath'];
};

export function getProductWidgetPageTitle({ asPath }: GetProductWidgetPageTitleParams) {
    const isViewAllProductsPage = asPath === ROUTES_URL.PRODUCT_LISTING;
    const isProductDetailsPage = asPath?.includes(ROUTES_URL.PRODUCT_DETAILS);
    const isCartPage = asPath === ROUTES_URL.CART;
    const isOrderHistoryPage = asPath === ROUTES_URL.ORDER_LISTING;
    const isReturnSummaryPage = asPath === ROUTES_URL.RETURNS;
    const isAccountSummaryPage =
        asPath?.includes(ROUTES_URL.PAYMENT_LISTING) &&
        asPath !== ROUTES_URL.MY_ACCOUNT_PAYMENT_SUMMARY &&
        asPath !== ROUTES_URL.MY_ACCOUNT_PAYMENT_STATEMENTS_ACCOUNT;

    if (isViewAllProductsPage) return 'View All Products page';

    if (isProductDetailsPage) return 'Product Details page';

    if (isCartPage) return 'Cart page';

    if (isOrderHistoryPage) return 'Order History page';

    if (isReturnSummaryPage) return 'Return Summary page';

    if (isAccountSummaryPage) return 'Account Summary page';

    return null;
}
